import { useState } from 'react';
import { Check } from 'lucide-react';
import { AuthForm } from '@/components/auth/AuthForm';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { OnboardingCarousel } from '@/components/OnboardingCarousel';
import { useI18n } from '@/contexts/I18nContext';
import logo from '@/assets/images/logo/logo.svg';

/** SOS-314: carousel kept in codebase but hidden on auth screens */
const SHOW_AUTH_INSTRUCTOR_CAROUSEL = false;

const Index = () => {
  const { t } = useI18n();
  // Sign-up is the default: this page is the ads landing destination.
  const [isSignUp, setIsSignUp] = useState(true);
  const [emailSentMessage, setEmailSentMessage] = useState<string | null>(null);

  const perks = [
    t('auth.brand.perk_track'),
    t('auth.brand.perk_community'),
    t('auth.brand.perk_free'),
  ];

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-sossiety-dark">
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-64 left-1/2 h-[520px] w-[760px] -translate-x-1/2 rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(215,46,22,0.18), transparent 62%)' }}
      />

      <div className="relative z-10 grid min-h-[100dvh] lg:grid-cols-2">
        {/* ── Brand panel (desktop only) ─────────────────────────────── */}
        <aside className="relative hidden overflow-hidden border-r border-white/10 bg-gradient-to-br from-[#2a1410] to-sossiety-dark p-14 lg:flex lg:flex-col lg:justify-between">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-40 -right-40 h-[480px] w-[480px] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(215,46,22,0.22), transparent 60%)' }}
          />
          <img src={logo} alt="sOSSiety" className="relative z-10 h-8 w-auto" />

          <div className="relative z-10 max-w-md">
            <h1 className="mb-4 text-4xl font-extrabold leading-tight tracking-tight text-white">
              {t('auth.brand.headline')}{' '}
              <span className="text-sossiety-red">{t('auth.brand.headline_accent')}</span>
            </h1>
            <p className="text-lg leading-relaxed text-white/60">{t('auth.brand.subtitle')}</p>

            <ul className="mt-8 space-y-4">
              {perks.map((perk) => (
                <li key={perk} className="flex items-center gap-3 text-[15px] text-white/80">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-sossiety-red/40 bg-sossiety-red-surface">
                    <Check className="h-3.5 w-3.5 text-sossiety-red-500" />
                  </span>
                  {perk}
                </li>
              ))}
            </ul>
          </div>

          <div />
        </aside>

        {/* ── Form panel ─────────────────────────────────────────────── */}
        <main className="flex flex-col items-center px-4 py-8 sm:px-6">
          <div className="flex w-full max-w-md items-center justify-between pb-6">
            <img src={logo} alt="sOSSiety" className="h-7 w-auto lg:invisible" />
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="hidden text-sm text-white/50 sm:inline">
                {isSignUp ? t('auth.have_account') : t('auth.no_account')}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                shape="pill"
                onClick={() => setIsSignUp((v) => !v)}
              >
                {isSignUp ? t('auth.sign_in') : t('auth.sign_up')}
              </Button>
              <LanguageSelector />
            </div>
          </div>

          <div className="flex w-full max-w-md flex-1 flex-col">
            {SHOW_AUTH_INSTRUCTOR_CAROUSEL && !isSignUp && <OnboardingCarousel />}

            {emailSentMessage && (
              <div className="relative mb-4 rounded-xl border border-yellow-400 bg-yellow-100/80 px-4 py-3 pr-8 text-center text-sm font-medium text-yellow-900">
                <button
                  type="button"
                  onClick={() => setEmailSentMessage(null)}
                  className="absolute right-2 top-2 text-lg font-bold text-yellow-900 hover:text-yellow-700 focus:outline-none"
                  aria-label="Fechar"
                >
                  ×
                </button>
                {emailSentMessage}
              </div>
            )}

            <AuthForm
              isSignUp={isSignUp}
              setIsSignUp={setIsSignUp}
              onEmailSent={(msg) => { setEmailSentMessage(msg); setIsSignUp(false); }}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Index;
